import LinkButton from '@app/src/components/LinkButton'
import { getHostnameFromUrl } from '@app/src/pages/Questionnaire/Accessor/Report/urlparser'
import type { Provider } from '@app/src/types/organizations'
import type { ScrapedAnswerCitation } from '@app/src/types/resourceExplorer'
import { OpenInNew, PublicOutlined, VerifiedOutlined } from '@mui/icons-material'
import { Box, Chip, Divider, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface OldScraperAnswerCitationProps {
  citations: ScrapedAnswerCitation[]
  provider: Provider | undefined
}

enum CitationVerificationState {
  Unknown,
  OtherWebsite,
  CompanyWebsite,
}

const getCitationVerificationState = (
  citation: ScrapedAnswerCitation,
  provider: Provider | undefined,
): CitationVerificationState => {
  const citationHostname = getHostnameFromUrl(citation.url)
  const providerHostname = getHostnameFromUrl(provider?.websiteAddress)
  if (!citationHostname || !providerHostname) return CitationVerificationState.Unknown
  return citationHostname === providerHostname
    ? CitationVerificationState.CompanyWebsite
    : CitationVerificationState.OtherWebsite
}
/**
 * @deprecated Use AnswerSourcesPanel instead
 */
const OldScrapedAnswerCitations: React.FC<OldScraperAnswerCitationProps> = ({ citations, provider }) => {
  const { formatMessage } = useIntl()
  const { palette } = useTheme()

  return (
    <Stack alignItems="baseline">
      {citations.map(citation => {
        const citationVerificationState = getCitationVerificationState(citation, provider)
        return (
          <React.Fragment key={`${citation.text}-${citation.url}`}>
            <Divider sx={{ mt: 3, mb: 3, width: '100%', color: palette.communication.light }} />
            <Stack direction="row" spacing={1} mb={2}>
              <Typography color="textPrimary">
                {formatMessage({ id: 'reporting.citations.title' }, { citationType: citation.citationType })}
              </Typography>
            </Stack>
            <Typography color="textSecondary" mb={3}>
              {citation.text}
            </Typography>
            <Box display="flex" justifyContent="space-between" width="100%">
              {citationVerificationState !== CitationVerificationState.Unknown ? (
                <Tooltip
                  title={
                    <Link href={citation.url} target="_blank" rel="noopener noreferrer">
                      {citation.url}
                    </Link>
                  }
                  arrow
                >
                  <Chip
                    sx={({ palette }) => ({
                      backgroundColor: 'transparent',
                      border: `1px solid ${palette.grey['A400']}`,
                    })}
                    icon={
                      citationVerificationState === CitationVerificationState.CompanyWebsite ? (
                        <VerifiedOutlined />
                      ) : (
                        <PublicOutlined />
                      )
                    }
                    label={formatMessage({
                      id:
                        citationVerificationState === CitationVerificationState.CompanyWebsite
                          ? 'reporting.citations.companyWebsite'
                          : 'reporting.citations.otherWebsite',
                    })}
                  />
                </Tooltip>
              ) : (
                <Box></Box>
              )}
              <LinkButton
                key={citation.url}
                variant="text"
                size="small"
                fullWidth={false}
                startIcon={<OpenInNew />}
                target="_blank"
                rel="noopener noreferrer"
                to={{ pathname: citation.url }}
              >
                {formatMessage({ id: 'reporting.citations.linkToSource' })}
              </LinkButton>
            </Box>
          </React.Fragment>
        )
      })}
    </Stack>
  )
}

export default OldScrapedAnswerCitations
